import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAppSettings } from '../hooks/useAppSettings';
import { AuthContext } from './AuthContext';

export interface CategoryDocument {
    id: number;
    filePath: string;
    category: string;
    orderNr: number;
    lastUpdatedUtc: string;
}

interface CategoryDocumentsContextType {
    documents: CategoryDocument[];
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    getDocumentsByCategory: (category: string) => CategoryDocument[];
}

const CategoryDocumentsContext = createContext<CategoryDocumentsContextType | undefined>(undefined);

interface CategoryDocumentsProviderProps {
    children: React.ReactNode;
}

export const CategoryDocumentsProvider: React.FC<CategoryDocumentsProviderProps> = ({ children }) => {
    // Always call all hooks unconditionally in the same order
    const { token } = useContext(AuthContext);
    const { settings: appSettings } = useAppSettings();
    const [documents, setDocuments] = useState<CategoryDocument[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchDocuments = useCallback(async () => {
        if (!appSettings?.api) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${appSettings.api}/GetCategoryDocuments`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch documents (${response.status})`);
            }

            const data: CategoryDocument[] = await response.json();
            setDocuments(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load category documents');
            console.error('Error fetching category documents:', err);
        } finally {
            setIsLoading(false);
        }
    }, [appSettings?.api, token]);

    // Load documents on mount when settings are available
    useEffect(() => {
        if (appSettings?.api && token) {
            fetchDocuments();
        }
    }, [appSettings?.api, token, fetchDocuments]);

    // Filter documents by category
    const getDocumentsByCategory = useCallback(
        (category: string): CategoryDocument[] => {
            return documents.filter((doc) => doc.category?.toUpperCase() === category.toUpperCase());
        },
        [documents]
    );

    return (
        <CategoryDocumentsContext.Provider
            value={{
                documents,
                isLoading,
                error,
                refetch: fetchDocuments,
                getDocumentsByCategory,
            }}
        >
            {children}
        </CategoryDocumentsContext.Provider>
    );
};

export const useCategoryDocumentsContext = (): CategoryDocumentsContextType => {
    const context = useContext(CategoryDocumentsContext);
    if (context === undefined) {
        throw new Error('useCategoryDocumentsContext must be used within a CategoryDocumentsProvider');
    }
    return context;
};
